<template>
  <div>
    <AlertDismiss ref="alertDismiss"/>
    <b-overlay :show="isFetching">
      <b-form @submit.prevent="handleSubmit" class="row">
        <div class="mb-2">
          <label class="form-label mb-0" for="name">Nombre de la plantilla</label>
          <input
              type="text"
              id="name"
              class="form-control"
              v-model="description"
              placeholder="Ingrese una descripción para la plantilla"
              required
          >
        </div>

        <MultiAutocomplete
          class="mb-1"
          v-model="recipients"
          @add="addRecipient"
          @remove="removeRecipient"
          :allowCustom="false"
          :items="mails"
          :placeholder="'Ingrese el email de los destinatarios (deben ser usuarios de la aplicación)'"
          invalidValueMsg="Ingrese un correo eléctronico válido"
          label="Destinatarios"
          inputType="email"
        />

        <div class="me-2 mb-2">
        <label>Roles destinatarios:</label>
        <select multiple class="form-control" v-model="selectedRoleIds" :size="selectSize">
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.name }}
          </option>
        </select>

        </div>

        <div class="mb-3">
          <label for="fileInput">Documento</label>
          <input
              type="file"
              class="form-control"
              id="fileInput"
              ref="fileInput"
              @change="handleFileChange"
              required
              accept=".pdf,application/pdf"
          >
        </div>

        <ListEdit
            v-model="modelValue"
            label="Preguntas de la plantilla"
            @handleAdd="handleAdd"
            @handleRemove="handleRemove"
            :minItems="1"
        >
          <template #default="{ index }">
            <div class="d-flex align-items-center">
              <div v-b-tooltip.hover.right :title="getTooltip(index)" class="me-2">
                <input type="checkbox" class="me-1" v-model="modelValue[index].deepening">
              </div>
              <input
                  class="flex-grow-1 form-control form-control-sm"
                  type="text"
                  v-model="modelValue[index].name"
                  :placeholder="'Pregunta ' + (index + 1)"
                  required
              />
            </div>
          </template>

        </ListEdit>

        <div class="mt-2 row">
          <div class="col-auto">
            <b-button type="submit" variant="primary" :disabled="isUploading">
              <b-spinner small v-if="isUploading"></b-spinner>
              Crear
            </b-button>
          </div>
          <div class="col">
            <ProgressCustom ref="progress"/>
          </div>
        </div>

      </b-form>

    </b-overlay>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import ListEdit from '@/components/shared/List-edit.vue'
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import ProgressCustom from '@/components/shared/Progress-custom.vue';
import {EventNames} from "@/plugins/enums";
import MultiAutocomplete from "@/components/shared/Autocomplete-input.vue";

export default {
  name: 'StatementTemplateForm',

  components: {
    AlertDismiss,
    ListEdit,
    ProgressCustom,
    MultiAutocomplete
  },

  data() {
    return {
      description: '',
      route: null,
      recipients: [],
      modelValue: [],
      selectedRoleIds: [],

      file: null,
      mails: [],
      roles: [],
      isUploading: false,
      isFetching: false,
    }
  },

  computed: {
    selectSize() {
      return this.roles.length
    }
  },

  async mounted() {
    this.isFetching = true  
    this.handleAdd()
    await this.getMails()
    await this.getRoles()
    this.isFetching = false
  },

  methods: {
    ...mapActions([
      'saveStatementDocument',
      'saveStatementTemplate',
      "getAllMails",
      "allRoles",
     ]),

    async handleSubmit() {
      this.isUploading = true;

      this.$refs.progress.progress(15, 'Subiendo documento...')
      let uploadedDocument = await this.uploadDocument();
      if (!uploadedDocument) {
        this.isUploading = false;
        this.$refs.progress.progress(0);
        return;
      }

      this.$refs.progress.progress(70, 'Creando plantilla...')

      let templateResult = await this.uploadTemplate()
      if (!templateResult) {
        this.$refs.progress.progress(0)
        this.isUploading = false
        return
      }

      this.$refs.progress.progress(100, 'Proceso completado')
      this.$refs.alertDismiss.showAlert('Plantilla creada con éxito', 'success')
      this.$eventBus.$emit(EventNames.CreatedStatementTemplate)
      setTimeout(() => {
        this.$refs.progress.progress(0)
      }, 1000)

      this.clearForm();
    },

    async getMails() {
      let result = await this.getAllMails();

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        return;
      }

      this.mails = result.data.map((x) => {
        return {
          id: x.id,
          name: x.mail
        }
      })
    },

    async getRoles() {
      let result = await this.allRoles();

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        return;
      }

      this.roles = result.data;
    },

    async uploadTemplate() {
      let result = await this.saveStatementTemplate({
        name: this.description,
        questions: this.modelValue,
        documentRoute: this.route,
        addresseeIds: this.recipients.map(x => x.id),
        addresseeRoleIds: this.selectedRoleIds,
      });

      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, 'warning');
        this.isUploading = false;
        return false;
      }
      return true;
    },

    async uploadDocument() {
      // if we have uploaded the document, we don't need to upload it again
      if (this.route) {
        return true
      }
      let result = await this.saveStatementDocument(this.file);
      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, 'warning');
        this.isUploading = false;
        return false;
      }

      this.route = result.data;
      return true;
    },

    clearForm() {
      this.isUploading = false
      this.description = '';
      this.modelValue = [];
      this.handleAdd();
      this.route = null;
      this.file = null;
      this.$refs.fileInput.value = null;
      this.recipients = [];
      this.selectedRoleIds = [];
    },

    handleAdd() {
      this.modelValue.push({name: null, deepening: false})
    },

    handleRemove(index) {
      this.modelValue.splice(index, 1)
    },

    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.file = file
      }
    },

    getTooltip(index) {
      if (this.modelValue[index].deepening) {
        return "Profundización obligatoria"
      }
      return "Profundización opcional"
    },

    addRecipient(recipient) {
      this.recipients.push(recipient)
    },

    removeRecipient(recipient) {
      this.recipients.splice(this.recipients.indexOf(recipient), 1)
    }
  },

  watch: {
    file() {
      this.route = null;
    }
  }
}
</script>
